import {
  TextField,
  Switch,
  FormControlLabel,
  Box,
  IconButton,
  Checkbox,
  FormGroup,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useState } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

const CheckboxQuestion = ({ question, updateQuestion, canEdit }) => {
  const { t } = useTranslation();
  const [optionValue, setOptionValue] = useState("");

  const addOption = () => {
    if (!optionValue.trim()) return;
    const arr = question.options ? question.options.split(";") : [];
    const updated = [...arr, optionValue.trim()];
    updateQuestion({ ...question, options: updated.join(";") });
    setOptionValue("");
  };

  const removeOption = (index) => {
    const arr = question.options.split(";");
    arr.splice(index, 1);
    updateQuestion({ ...question, options: arr.join(";") });
  };

  const optionsArray = question.options ? question.options.split(";") : [];

  // --- READ-ONLY VIEW ---
  if (!canEdit) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
          {question.questionText || t("admin.formCreation.untitledQuestion")}
          {question.isRequired && (
            <Typography component="span" color="error" sx={{ ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>

        <FormGroup>
          {optionsArray.length > 0 ? (
            optionsArray.map((opt, idx) => (
              <FormControlLabel
                key={idx}
                control={<Checkbox disabled size="small" />}
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      display: "inline-block",
                      whiteSpace: "nowrap", // Prevent label from wrapping under checkbox
                    }}
                  >
                    {opt}
                  </Typography>
                }
                sx={{
                  pointerEvents: "none",
                  alignItems: "center", // Ensure vertical alignment
                }}
              />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {t("admin.formCreation.noOptionsDefined")}
            </Typography>
          )}
        </FormGroup>
      </Box>
    );
  }

  // --- EDIT VIEW ---
  return (
    <Box>
      {/* Question Text */}
      <TextField
        fullWidth
        label={t("admin.formCreation.checkbox")}
        value={question.questionText}
        onChange={(e) => updateQuestion({ ...question, questionText: e.target.value })}
        sx={{ mb: 2 }}
      />

      {/* Required Switch */}
      <FormControlLabel
        control={
          <Switch
            checked={question.isRequired}
            onChange={(e) => updateQuestion({ ...question, isRequired: e.target.checked })}
          />
        }
        label={t("admin.formCreation.required")}
      />

      {/* Add Options Input */}
      <Box display="flex" alignItems="center" sx={{ mb: 2, width: "100%", maxWidth: "500px" }}>
        <TextField
          label={t("admin.formCreation.newOption")}
          value={optionValue}
          onChange={(e) => setOptionValue(e.target.value)}
          sx={{ mr: 1, flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addOption();
            }
          }}
        />
        <MDButton onClick={addOption} color="primary" variant="outlined">
          {t("common.add")}
        </MDButton>
      </Box>

      {/* Options List */}
      <Box component="ul" sx={{ pl: 2, m: 0 }}>
        {optionsArray.map((opt, idx) => (
          <li
            key={idx}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}
          >
            <Typography variant="body2">{opt}</Typography>
            <IconButton size="small" color="error" onClick={() => removeOption(idx)}>
              <Delete fontSize="small" />
            </IconButton>
          </li>
        ))}
      </Box>
    </Box>
  );
};

CheckboxQuestion.propTypes = {
  question: PropTypes.shape({
    formId: PropTypes.number,
    questionText: PropTypes.string,
    fieldType: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    options: PropTypes.string,
    orderIndex: PropTypes.number,
  }).isRequired,
  updateQuestion: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default CheckboxQuestion;
